import { useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import useAsync from 'react-use/lib/useAsync';

import { migrationApiRef } from '../../migration/api';
import { cleanArray } from '../migrationServicePicker/MigrationServicePicker';

/**
 * Multi-select of teams, loaded from the migration database via the
 * migration backend.
 *
 * The backing route is devops-admins only, so a non-admin sees an explanatory
 * error here rather than an empty list they cannot explain.
 */
const MigrationTeamPickerFieldSchema = makeFieldSchema({
  output: z => z.array(z.string()),
  uiOptions: z =>
    z.object({
      onboarded: z
        .enum(['any', 'yes', 'no'])
        .optional()
        .describe(
          "Restrict the list by onboarding state. 'any' (the default) shows all in-scope database teams; onboarding indicates whether a catalog Group exists.",
        ),
    }),
});

export const MigrationTeamPickerSchema = MigrationTeamPickerFieldSchema;

type Props = typeof MigrationTeamPickerFieldSchema.TProps;

/** Two-line option row; Autocomplete's renderOption is plain JSX, not a List. */
function ListItemTextLike({
  primary,
  secondary,
}: {
  primary: string;
  secondary?: string;
}) {
  return (
    <span>
      <span style={{ display: 'block' }}>{primary}</span>
      {secondary && (
        <span style={{ display: 'block', opacity: 0.7, fontSize: '0.8em' }}>
          {secondary}
        </span>
      )}
    </span>
  );
}

export const MigrationTeamPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
}: Props) => {
  const migrationApi = useApi(migrationApiRef);
  const onboardedFilter = uiSchema['ui:options']?.onboarded ?? 'any';

  const { value, loading, error } = useAsync(
    () => migrationApi.listTeams(),
    [migrationApi],
  );

  const selected: string[] = cleanArray<string>(formData);
  // RJSF seeds the array to `minItems` with a placeholder before the operator
  // picks anything. Hiding it is not enough -- an untouched form would submit
  // that placeholder to the workflow -- so the cleaned value is written back
  // once. Guarded on length, so this cannot loop.
  useEffect(() => {
    if (Array.isArray(formData) && formData.length !== selected.length) {
      onChange(selected);
    }
  }, [formData, selected, onChange]);

  const teams = (value?.teams ?? []).filter(t => {
    if (onboardedFilter === 'yes') return t.onboarded;
    if (onboardedFilter === 'no') return !t.onboarded;
    return true;
  });

  const byName = new Map(
    teams.map(t => [
      t.name,
      `${t.serviceCount} service(s)${t.onboarded ? ' \u00b7 already onboarded' : ''}`,
    ]),
  );

  const helper = () => {
    if (loading) return 'Loading teams…';
    if (error) return `Could not load teams: ${error.message}`;
    if (teams.length === 0) {
      return onboardedFilter === 'no'
        ? 'Every in-scope team in the migration database is already onboarded.'
        : 'No teams found in the migration database.';
    }
    return `${selected.length} of ${teams.length} team(s) selected`;
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData?.length}
      fullWidth
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        id={idSchema?.$id}
        options={teams.map(t => t.name)}
        value={selected}
        disabled={loading || Boolean(error)}
        onChange={(_e, v) => onChange(v as string[])}
        getOptionLabel={name => name}
        renderOption={(name, { selected: isSel }) => (
          <>
            <Checkbox checked={isSel} style={{ marginRight: 8 }} />
            <ListItemTextLike primary={name} secondary={byName.get(name)} />
          </>
        )}
        renderTags={(v, getTagProps) =>
          v.map((name, index) => (
            <Chip
              {...getTagProps({ index })}
              key={name}
              label={name}
              size="small"
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Teams"
            placeholder={selected.length ? undefined : 'Type to filter teams…'}
          />
        )}
      />
      <Box mt={1}>
        <Button
          size="small"
          disabled={
            loading || Boolean(error) || selected.length === teams.length
          }
          onClick={() => onChange(teams.map(t => t.name))}
        >
          Select all ({teams.length})
        </Button>
        <Button
          size="small"
          disabled={selected.length === 0}
          onClick={() => onChange([])}
        >
          Clear
        </Button>
      </Box>
      <FormHelperText>{helper()}</FormHelperText>
    </FormControl>
  );
};
